/**
 * useResetPassword Composable
 * 
 * A composable that handles the password reset functionality.
 * It manages the form state, handles form submission, and provides error handling.
 * 
 * Features:
 * - Password form state management
 * - Form validation (password match, strength)
 * - Error handling and loading states
 * - Success message display
 * - Automatic redirect after successful reset
 * 
 * @returns {Object} An object containing form state, loading state, error state, and reset password method
 */

import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '@/services/api'

export function useResetPassword() {
  const router = useRouter()
  const route = useRoute()

  const password = ref('')
  const confirmPassword = ref('')
  const loading = ref(false)
  const error = ref('')
  const success = ref('')

  const handleResetPassword = async () => {
    try {
      loading.value = true
      error.value = ''
      success.value = ''

      // Validation
      if (!password.value || !confirmPassword.value) {
        throw new Error('Please fill in all fields')
      }

      if (password.value !== confirmPassword.value) {
        throw new Error('Passwords do not match')
      }

      if (password.value.length < 8) {
        throw new Error('Password must be at least 8 characters long')
      }

      // Get token from URL query parameter
      const token = route.query.token as string
      if (!token) {
        throw new Error('Invalid or missing reset token. Please request a new password reset link.')
      }

      // Make API call to backend
      const response = await api.post('/reset-password', {
        token: token,
        password: password.value,
        password_confirmation: confirmPassword.value
      })

      // Success response
      success.value = response.data.message || 'Password has been reset successfully.'

      // Clear form
      password.value = ''
      confirmPassword.value = ''

      // Redirect to brand selection after successful reset
      setTimeout(() => {
        router.push('/brands?message=Password reset successful. Please log in with your new password.')
      }, 2000)

    } catch (err: any) {
      console.error('Reset password error:', err)

      // Handle different types of errors
      if (err.response?.data?.errors) {
        // Handle validation errors from backend
        const errors = err.response.data.errors
        if (typeof errors === 'string') {
          error.value = errors
        } else if (typeof errors === 'object') {
          // If it's an object, get the first error message
          const firstError = Object.values(errors)[0]
          error.value = Array.isArray(firstError) ? firstError[0] : String(firstError)
        } else {
          error.value = 'Validation error occurred'
        }
      } else if (err.response?.data?.message) {
        error.value = err.response.data.message
      } else if (err.message) {
        error.value = err.message
      } else {
        error.value = 'Failed to reset password. Please try again.'
      }
    } finally {
      loading.value = false
    }
  }

  return {
    password,
    confirmPassword,
    loading,
    error,
    success,
    handleResetPassword
  }
}

